import { clamp } from "../../core/math";
import { smoothSet } from "../envelope";
import { HARD_MAX_VOICES, type Instrument } from "./types";

export type DrumVoice = "kick" | "snare" | "hat" | "clap";

/** GM percussion note → synthesized voice. Unmapped notes are ignored. */
export const DRUM_NOTE_MAP: Record<number, DrumVoice> = { 35: "kick", 36: "kick", 38: "snare", 40: "snare", 42: "hat", 44: "hat", 46: "hat", 39: "clap" };

export interface DrumKitParams {
  volume: number;
  tune: number; // semitones -12..12
  decay: number; // multiplier 0.25–2
}

export const DEFAULT_DRUMKIT_PARAMS: DrumKitParams = { volume: 0.8, tune: 0, decay: 1 };

interface Hit { sources: AudioScheduledSourceNode[]; nodes: AudioNode[]; }

/**
 * Procedurally synthesized drum kit — no samples, so no third-party asset
 * licences. Each hit allocates short-lived nodes that clean up on `ended`.
 */
export class DrumKitInstrument implements Instrument<DrumKitParams> {
  readonly kind = "drumkit";
  readonly output: GainNode;
  private _params: DrumKitParams;
  private hits = new Set<Hit>();
  private noise: AudioBuffer;

  constructor(private ctx: BaseAudioContext, params: Partial<DrumKitParams> = {}) {
    this._params = { ...DEFAULT_DRUMKIT_PARAMS, ...params };
    this.output = ctx.createGain();
    this.output.gain.value = this._params.volume;
    this.noise = makeNoise(ctx, 1);
  }

  get params(): DrumKitParams { return this._params; }
  get activeVoices(): number { return this.hits.size; }

  noteOn(note: number, velocity: number, timeSec = this.ctx.currentTime): void {
    const voice = DRUM_NOTE_MAP[note];
    if (!voice) return;
    if (this.hits.size >= HARD_MAX_VOICES) return; // runaway protection
    this.trigger(voice, clamp(velocity, 0, 1), timeSec);
  }

  /** Drums are one-shots; note-off is ignored. */
  noteOff(): void {}
  releaseAll(): void {}

  hardStop(): void {
    const now = this.ctx.currentTime;
    for (const h of [...this.hits]) {
      for (const s of h.sources) { try { s.stop(now); } catch { /* not started */ } }
      this.finish(h);
    }
  }

  setParams(params: Partial<DrumKitParams>): void {
    this._params = { ...this._params, ...params };
    if (params.volume !== undefined) smoothSet(this.output.gain, this._params.volume, this.ctx);
  }

  dispose(): void {
    this.hardStop();
    this.output.disconnect();
  }

  trigger(voice: DrumVoice, velocity: number, t: number): void {
    const ctx = this.ctx;
    const d = clamp(this._params.decay, 0.25, 2);
    const tune = Math.pow(2, clamp(this._params.tune, -12, 12) / 12);
    const amp = ctx.createGain();
    amp.connect(this.output);
    const hit: Hit = { sources: [], nodes: [amp] };
    const env = (g: AudioParam, peak: number, decaySec: number) => {
      g.setValueAtTime(0.0001, t);
      g.exponentialRampToValueAtTime(Math.max(peak, 0.0002), t + 0.002);
      g.exponentialRampToValueAtTime(0.0001, t + decaySec);
    };
    const noiseSrc = () => {
      const s = ctx.createBufferSource();
      s.buffer = this.noise;
      return s;
    };
    let end = t;
    switch (voice) {
      case "kick": {
        const o = ctx.createOscillator();
        o.frequency.setValueAtTime(150 * tune, t);
        o.frequency.exponentialRampToValueAtTime(45 * tune, t + 0.12);
        env(amp.gain, velocity, 0.45 * d);
        o.connect(amp);
        end = t + 0.45 * d;
        hit.sources.push(o);
        break;
      }
      case "snare": {
        const n = noiseSrc();
        const hp = ctx.createBiquadFilter();
        hp.type = "highpass";
        hp.frequency.value = 1200;
        const ng = ctx.createGain();
        env(ng.gain, velocity * 0.8, 0.22 * d);
        n.connect(hp).connect(ng).connect(amp);
        const o = ctx.createOscillator();
        o.type = "triangle";
        o.frequency.value = 185 * tune;
        const og = ctx.createGain();
        env(og.gain, velocity * 0.6, 0.1 * d);
        o.connect(og).connect(amp);
        amp.gain.value = 1;
        end = t + 0.22 * d;
        hit.sources.push(n, o);
        hit.nodes.push(hp, ng, og);
        break;
      }
      case "hat": {
        const n = noiseSrc();
        const hp = ctx.createBiquadFilter();
        hp.type = "highpass";
        hp.frequency.value = 7000;
        env(amp.gain, velocity * 0.5, 0.06 * d);
        n.connect(hp).connect(amp);
        end = t + 0.06 * d;
        hit.sources.push(n);
        hit.nodes.push(hp);
        break;
      }
      case "clap": {
        const n = noiseSrc();
        const bp = ctx.createBiquadFilter();
        bp.type = "bandpass";
        bp.frequency.value = 1500;
        bp.Q.value = 0.8;
        const g = amp.gain;
        g.setValueAtTime(0.0001, t);
        for (const off of [0, 0.012, 0.024]) {
          g.setValueAtTime(velocity * 0.8, t + off);
          g.exponentialRampToValueAtTime(0.05, t + off + 0.01);
        }
        g.exponentialRampToValueAtTime(0.0001, t + 0.2 * d);
        n.connect(bp).connect(amp);
        end = t + 0.2 * d;
        hit.sources.push(n);
        hit.nodes.push(bp);
        break;
      }
    }
    for (const s of hit.sources) { s.start(t); s.stop(end + 0.02); }
    hit.sources[0]!.onended = () => this.finish(hit);
    this.hits.add(hit);
  }

  private finish(h: Hit): void {
    if (!this.hits.delete(h)) return;
    for (const s of h.sources) s.disconnect();
    for (const n of h.nodes) n.disconnect();
  }
}

export function makeNoise(ctx: BaseAudioContext, seconds: number): AudioBuffer {
  const len = Math.max(1, Math.floor(ctx.sampleRate * seconds));
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
  return buf;
}
